import React, { FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBarType, MessageBar, PrimaryButton } from '@fluentui/react';
import { TextField, required, Form, Language } from '@eta/components';
import { ConnectedProps, connect } from 'react-redux';
import { isNull } from 'util';
import { acceptInvitation } from '../Redux/Actions';
import { ReduxState } from '../../common/reducers';
import SuccessInvitation from './SuccessInvitation';
import {
    CheckDelegationInvitationValidityQueryResponse,
    AccetpInvitationDto,
} from '../../Services/ClientProxy/clientProxy';

import './ExistingAccount.scss';

const mapStateToProps = (state: ReduxState) => ({
    lastError: state.invitation.lastError,
    currentLanguage: state.reduxlanguage.language,
});

const mapDispatchToProps = {
    acceptInvitation,
};

export interface Props {
    invitationId: string;
    invitationData: CheckDelegationInvitationValidityQueryResponse;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ExistingAccount: FC<PropsFromRedux & Props> = (
    props: PropsFromRedux & Props
) => {
    const { invitationData, lastError, currentLanguage: propsCurrentLanguage } = props;
    const [currentInvitation] = useState(invitationData);
    const [invitationRequest, setInvitationRequest] = useState(
        new AccetpInvitationDto()
    );

    const [currentLanguage, setCurrentLanguage] = useState<Language>();


    const { t } = useTranslation(['resources', 'common']);
    const [, setIsLoading] = useState<boolean>(true);
    const [showMessageSuccess, setShowMessageSuccess] = useState<boolean>(
        false
    );
    const [error, setError] = useState<string>();
    const [completed, setCompleted] = useState<boolean>(false);

    const form = useRef(new Form({}));
    const [isFormValid, setIsFormValid] = useState<boolean>(
        form.current.isValid
    );

    form.current.onValidityChanged = (isValid) => setIsFormValid(isValid);
    useEffect(() => {

        if (propsCurrentLanguage !== currentLanguage) {
            setCurrentLanguage(propsCurrentLanguage);
        }
    }, [currentLanguage, propsCurrentLanguage]);
    useEffect(() => {
        if (lastError) {
            setShowMessageSuccess(false);
        }
        setError(lastError?.serviceError?.toString());
    }, [lastError]);

    const saveForm = async () => {
        if (form.current.isValid) {
            try {
                setIsLoading(true);

                const response = await props.acceptInvitation(
                    props.invitationId,
                    invitationRequest
                );

                if (isNull((await response.payload).lastError)) {
                    setCompleted(true);
                }
            } catch (exception) {
                setError(exception.message);
            }
            setIsLoading(false);
        }
    };

    return (
        <div>
            {!completed && (
                <div className="invContent">
                    {showMessageSuccess && (
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            isMultiline={false}
                            onDismiss={() => setShowMessageSuccess(false)}
                        >
                            {t('myProfile:successSaved')}
                        </MessageBar>
                    )}
                    {error && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            onDismiss={() => setError('')}
                        >
                            {error}
                        </MessageBar>
                    )}

                    <div>
                        <div className="title">
                            <p>{t('form-components.ETA')}</p>
                        </div>
                    </div>
                    <div>
                        <div>
                            <h5>
                                {t('form-components.WelcomeMessage6')}{' '}
                                {currentLanguage?.langIso4.startsWith('ar') ? `${currentInvitation.nameSecondaryLang} ${currentInvitation.surnameSecondaryLang}` 
                                : `${currentInvitation?.namePrimaryLang || ''} ${currentInvitation?.surnamePrimaryLang || ''}`}
                            </h5>
                            <p className="W-invitation">
                                {t('form-components.WelcomeMessage')}
                            </p>
                            <div className="messageContent">
                                <p>
                                    {t('form-components.WelcomeMessage2')}
                                    {t('form-components.Admin.Name')}
                                    {t('form-components.WelcomeMessage3')}
                                    {t(currentInvitation.role || '')}
                                    {t('form-components.WelcomeMessage4')}
                                    {currentInvitation.taxpayerNameAr}
                                </p>
                                <p>{t('form-components.WelcomeMessage5')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="natID">
                        <TextField
                            label={t('UserProfile.NationalID')}
                            value={invitationRequest.nationalID}
                            onChange={(_, v) =>
                                setInvitationRequest(
                                    new AccetpInvitationDto({
                                        ...invitationRequest,
                                        nationalID: v ?? '',
                                    })
                                )
                            }
                            validationFunc={(v) =>
                                required(t, v, t('UserProfile.NationalID'))
                            }
                            toolTip={t('UserProfile.NationalID')}
                        />
                    </div>
                    <div className="btnComplete">
                        <PrimaryButton
                            text={t('form-components.Continue')}
                            onClick={() => saveForm()}
                            disabled={!isFormValid}
                        />
                    </div>
                </div>
            )}
            {completed && <SuccessInvitation invitationData={invitationData} />}
        </div>
    );
};

export default connector(ExistingAccount);
